import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';

/**
 * Parrot assignment modal for reassigning parrots to different users
 */
const ParrotAssignmentModal = ({
  isOpen,
  onClose,
  parrot,
  onAssignmentComplete
}) => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [assignmentType, setAssignmentType] = useState('assign'); // 'assign' or 'unassign'

  /**
   * Fetch users for assignment dropdown
   */
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const token = await getToken();
      
      const response = await apiRequest('/api/admin/users?limit=100&sortBy=first_name&sortOrder=asc', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  /**
   * Handle assignment submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (assignmentType === 'assign' && !selectedUserId) {
      showToast('Please select a user to assign this parrot to', 'error');
      return;
    }

    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await apiRequest(`/api/admin/parrots/${parrot.id}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: assignmentType === 'assign' ? selectedUserId : null,
          action: assignmentType
        })
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const updatedParrot = await response.json();
      onAssignmentComplete(updatedParrot);
      handleClose();
      
      const message = assignmentType === 'assign' 
        ? 'Parrot assigned successfully' 
        : 'Parrot unassigned successfully';
      showToast(message, 'success');

    } catch (error) {
      console.error('Error updating parrot assignment:', error);
      showToast(error.message || 'Failed to update parrot assignment', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    setSelectedUserId('');
    setAssignmentType('assign');
    onClose();
  };

  /**
   * Get current owner display name
   */
  const getCurrentOwnerName = () => {
    if (!parrot) return 'Unknown';
    return parrot.user_name || parrot.user_email || 'Unassigned';
  };

  // Load users when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      // Set initial values based on current parrot state
      if (parrot?.user_id) {
        setSelectedUserId(parrot.user_id);
        setAssignmentType('assign');
      } else {
        setAssignmentType('assign');
      }
    }
  }, [isOpen, parrot]);

  if (!isOpen || !parrot) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-md">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-medium text-white dark:text-white">Assign Parrot</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white dark:text-white"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Parrot Info */}
          <div className="mb-6 p-4 bg-gray-700 rounded-lg">
            <h4 className="text-sm font-medium text-white mb-2 dark:text-white">Parrot Details</h4>
            <div className="text-sm text-gray-300 dark:text-gray-600">
              <div><strong>Name:</strong> {parrot.name || 'Unnamed Parrot'}</div>
              <div><strong>Current Owner:</strong> {getCurrentOwnerName()}</div>
              <div><strong>Status:</strong> {parrot.status || 'Active'}</div>
            </div>
          </div>

          {/* Assignment Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Assignment Type */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 dark:text-gray-600">
                Assignment Action
              </label>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="assign"
                    name="assignmentType"
                    value="assign"
                    checked={assignmentType === 'assign'}
                    onChange={(e) => setAssignmentType(e.target.value)}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-600 bg-gray-700"
                  />
                  <label htmlFor="assign" className="ml-2 text-sm text-gray-300 dark:text-gray-600">
                    Assign to a user
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="unassign"
                    name="assignmentType"
                    value="unassign"
                    checked={assignmentType === 'unassign'}
                    onChange={(e) => setAssignmentType(e.target.value)}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-600 bg-gray-700"
                  />
                  <label htmlFor="unassign" className="ml-2 text-sm text-gray-300 dark:text-gray-600">
                    Remove user assignment (make admin-only)
                  </label>
                </div>
              </div>
            </div>

            {/* User Selection */}
            {assignmentType === 'assign' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
                  Select User
                </label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white"
                  disabled={loadingUsers}
                  required
                >
                  <option value="">Select a user...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.first_name && user.last_name 
                        ? `${user.first_name} ${user.last_name} (${user.email})`
                        : user.email
                      }
                    </option>
                  ))}
                </select>
                {loadingUsers && (
                  <p className="text-gray-400 text-sm mt-1">Loading users...</p>
                )}
              </div>
            )}

            {/* Warning for unassignment */}
            {assignmentType === 'unassign' && (
              <div className="p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-300 text-sm">
                  <strong>Warning:</strong> Removing user assignment will make this parrot accessible only to administrators. 
                  The user will lose access to this parrot and its associated phone numbers.
                </p>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors dark:text-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || (assignmentType === 'assign' && !selectedUserId)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center space-x-2 dark:text-white"
              >
                {loading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <span>
                  {loading 
                    ? 'Processing...' 
                    : assignmentType === 'assign' 
                      ? 'Assign Parrot' 
                      : 'Remove Assignment'
                  }
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ParrotAssignmentModal;
